// BB-0 (docs/build_bot_platform.md §3.2.1): retry/timeout/backoff contract for
// client model pass-through calls. Mirrors the contract the node server must
// implement in modelRouter.chat (docs/backend.md §7.4): 30s per-attempt timeout,
// 3 retries, exponential backoff 1s/2s/4s + jitter. No content is stored here —
// this wraps the pass-through call only.

export const MODEL_RETRY_POLICY = Object.freeze({
  timeoutMs: 30_000,
  retries: 3,
  backoffBaseMs: 1_000,
  backoffFactor: 2,
  jitterRatio: 0.25
});

// Error codes classified as transient (retryable): provider overload, upstream
// timeouts, and 5xx-family gateway failures. Everything else (4xx auth/plan/
// policy, aborted user actions) fails fast without burning retries.
const RETRYABLE_CODES = new Set([
  'MODEL_TIMEOUT',
  'MODEL_OVERLOADED',
  'MODEL_UNAVAILABLE',
  'MODEL_GATEWAY_ERROR',
  'NETWORK_ERROR'
]);

export function isRetryableError(error) {
  if (error?.name === 'AbortError') return true; // timeout abort — retry once counts as a fresh attempt
  if (error?.code === 'CONTENT_POLICY_BLOCKED') return false; // deterministic — retrying can't help
  return Boolean(error?.code && RETRYABLE_CODES.has(error.code));
}

function classifyStatusError(status) {
  if (status === 429) return 'MODEL_OVERLOADED';
  if (status >= 500) return 'MODEL_UNAVAILABLE';
  return 'MODEL_REQUEST_REJECTED';
}

export function withModelError(error, { timeoutMs } = {}) {
  const timedOut = error?.name === 'AbortError';
  const status = Number(error?.status || 0);

  return Object.assign(error || new Error('Model call failed'), {
    code: error?.code
      || (timedOut ? 'MODEL_TIMEOUT' : classifyStatusError(status)),
    timeoutMs: timeoutMs || MODEL_RETRY_POLICY.timeoutMs,
    retryable: isRetryableError(
      Object.assign(error || new Error('Model call failed'), {
        code: error?.code || (timedOut ? 'MODEL_TIMEOUT' : classifyStatusError(status))
      })
    )
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Deterministic-per-attempt jitter: full jitter within ±jitterRatio of the
// computed backoff, so parallel sends don't thundering-herd a recovering
// provider. Kept synchronous-testable via injectable random.
export function computeBackoffMs(attempt, {
  backoffBaseMs = MODEL_RETRY_POLICY.backoffBaseMs,
  backoffFactor = MODEL_RETRY_POLICY.backoffFactor,
  jitterRatio = MODEL_RETRY_POLICY.jitterRatio,
  random = Math.random
} = {}) {
  const delay = backoffBaseMs * backoffFactor ** attempt;
  const jitter = delay * jitterRatio * (random() * 2 - 1);

  return Math.max(0, Math.round(delay + jitter));
}

// Dev/QA seam (docs/build_bot_platform.md §4 BB-0 "Done when"): inject provider
// faults (timeout / 5xx / overload / network) into the pass-through so retry + consult
// degradation can be proven without a real provider. Set:
//   localStorage.setItem('bb_fault_model', 'timeout|5xx|overload|network')
//   localStorage.setItem('bb_fault_consult', '5xx')   // hard-down consult
// Optional recovery (fault clears after N failed attempts — proves retry works):
//   localStorage.setItem('bb_fault_model_recover_after', '2')
// Optional fast backoff for automated tests/dev (ms, e.g. '5'):
//   localStorage.setItem('bb_backoff_base_ms', '5')
// Clear both keys to restore healthy behaviour.
export function getInjectedFault(scope = 'model', attempt = 0) {
  try {
    const key = scope === 'consult' ? 'bb_fault_consult' : 'bb_fault_model';
    const recoverAfter = Number(localStorage.getItem(`${key}_recover_after`) || 0);

    if (recoverAfter > 0 && attempt >= recoverAfter) return null;

    const fault = localStorage.getItem(key);
    const modes = new Set(['timeout', '5xx', 'overload', 'network']);

    return modes.has(fault) ? fault : null;
  } catch {
    return null;
  }
}

// Test/dev-only backoff override; empty in production.
export function getDevBackoffOverrides() {
  try {
    const baseMs = Number(localStorage.getItem('bb_backoff_base_ms') || 0);
    return baseMs > 0 ? { backoffBaseMs: baseMs, jitterRatio: 0 } : {};
  } catch {
    return {};
  }
}

export function makeFaultError(fault) {
  if (fault === 'timeout') {
    const error = new Error('Provider request timed out.');
    error.name = 'AbortError';
    error.code = 'MODEL_TIMEOUT';
    return error;
  }
  if (fault === 'overload') {
    const error = new Error('Provider rate limit reached (429).');
    error.status = 429;
    error.code = 'MODEL_OVERLOADED';
    return error;
  }
  if (fault === 'network') {
    const error = new Error('Network unreachable.');
    error.code = 'NETWORK_ERROR';
    return error;
  }
  const error = new Error('Provider internal error (5xx).');
  error.status = 503;
  error.code = 'MODEL_UNAVAILABLE';
  return error;
}

/**
 * Run an async model-call task under the BB-0 retry contract.
 * - 30s per-attempt timeout via AbortController-compatible racing (works for
 *   fetch-style calls that accept a signal; mock calls are simply raced).
 * - Up to `retries` additional attempts on retryable faults.
 * - Exponential backoff 1s/2s/4s + jitter between attempts.
 * Returns the task result; throws the classified error after exhaustion.
 */
export async function withModelRetry(task, {
  timeoutMs = MODEL_RETRY_POLICY.timeoutMs,
  retries = MODEL_RETRY_POLICY.retries,
  backoff = {},
  now = Date.now
} = {}) {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await sleep(computeBackoffMs(attempt - 1, backoff));
    }

    const startedAt = now();

    try {
      const result = await Promise.race([
        task(attempt, startedAt),
        sleep(timeoutMs).then(() => {
          const error = new Error(`Model call exceeded ${timeoutMs}ms.`);
          error.name = 'AbortError';
          error.code = 'MODEL_TIMEOUT';
          throw error;
        })
      ]);

      return result;
    } catch (error) {
      lastError = withModelError(error, { timeoutMs });

      if (!lastError.retryable || attempt === retries) {
        throw lastError;
      }
    }
  }

  throw lastError;
}
